/**
 * Price list and quotes (Del 2 DATA-1027, Del 1 UX-1059, owner decisions 2026-10-02 … 2026-10-06). Pure functions shared
 * by the API and the web: the list price for a package, the volume tier, the ads management fee and the summary of a quote
 * (offert) with its lines, totals and what is still missing. Amounts are whole kronor excluding VAT.
 */

import type { TravelRule } from './travel.js';

export type AgreementKind = 'subscription' | 'project';

export interface PackageDef {
  key: string;
  en: string;
  sv: string;
  /** Monthly price for a subscription; null = custom (Akerman) */
  monthly: number | null;
  /** Custom package (Akerman): no list price, priced and described per agreement */
  custom?: boolean;
  /** Subscription contents (from KFF's quote templates); absent = the per-production contents apply per month */
  shootH?: number;
  filmsSv?: string;
  lengthMin?: number;
  gfxSv?: string;
  actorsSv?: string;
  /** Per-production contents in Swedish (project-based agreements) */
  project?: { videos: string; actors: string; gfx: string; delivery: string } | null;
}

/** Volume tier for project-based agreements: price per production at a minimum number of productions. */
export interface TierDef {
  min: number;
  /** Discount label in percent (0 = list price) */
  pct: number;
  prices: Record<string, number>;
}

export interface AddOnDef {
  key: string;
  en: string;
  sv: string;
  price: number;
  unit: 'h' | null;
  /** "from 5 000" */
  from: boolean;
  /** Ticked in new quotes unless removed (owner 2026-10-16: options in the agreement, some on by default) */
  defaultOn?: boolean;
}

export interface PriceListData {
  packages: PackageDef[];
  tiers: TierDef[];
  addOns: AddOnDef[];
  startup: Record<AgreementKind, number>;
  /** Ads management: fixed per month + pct % of the media budget, the variable part counted on at most `cap` */
  ads: { fixed: number; pct: number; cap: number };
  /** What every package includes */
  included: { en: string; sv: string };
}

export interface PriceList extends PriceListData {
  version: number;
  validFrom: string;
  note: string;
  createdAt: string;
  createdBy: string | null;
}

/** Version 1: KFF's list as decided by the owner 2026-10-02 … 2026-10-06 (no derived prices left). */
export const PRICE_LIST_SEED: PriceListData = {
  packages: [
    { key: 'Bergman', en: 'Basic', sv: 'Bas', monthly: 30000, shootH: 2, filmsSv: '1–4 redigerade filmer', lengthMin: 10, gfxSv: 'Bas', actorsSv: 'skådespelare ingår inte',
      project: { videos: 'fyra korta eller en större per produktion', actors: 'ingår ej', gfx: 'Bas – titlar, undertexter & logotyp', delivery: '5 dagar' } },
    { key: 'Spielberg', en: 'Medium', sv: 'Medel', monthly: 45000, shootH: 4, filmsSv: '1–4 redigerade filmer', lengthMin: 5, gfxSv: 'Utökad', actorsSv: 'skådespelare ingår',
      project: { videos: 'dubbla Bergmans volym + cutdowns & versioner', actors: 'ingår – ingen medarbetare behöver stå i bild', gfx: 'Utökad – motion graphics & animerade element', delivery: '5 dagar' } },
    { key: 'Hitchcock', en: 'Advanced', sv: 'Avancerad', monthly: 60000, shootH: 8, filmsSv: '1–2 redigerade filmer', lengthMin: 2, gfxSv: 'Omfattande', actorsSv: 'skådespelare ingår',
      project: { videos: 'en signaturproduktion', actors: 'ingår – castade för projektet', gfx: 'Avancerad', delivery: 'enligt gemensam projektplan' } },
    { key: 'Kubrick', en: 'Exclusive', sv: 'Exklusivt', monthly: 120000,
      project: { videos: 'hela contentbehovet – video, annonser & statiskt', actors: 'alltid inkluderade', gfx: 'Avancerad – eget material animerat', delivery: '5 dagar – alltid först i kön' } },
    { key: 'Scott', en: 'Advertising', sv: 'Annonsering', monthly: 30000, shootH: 3, filmsSv: '1–3 annonsfilmer och 2 cutdowns', lengthMin: 2, gfxSv: 'Bas', actorsSv: 'statister ingår',
      project: { videos: '1–3 annonsfilmer och 2 cutdowns med en total längd upp till 2 minuter, inspelning upp till 3 timmar', actors: 'statister ingår', gfx: 'Bas', delivery: 'enligt gemensam projektplan' } },
    { key: 'Lucas', en: 'Editing', sv: 'Klippning', monthly: 20000, shootH: 0, filmsSv: '1–4 redigerade filmer', lengthMin: 5, gfxSv: 'Bas', actorsSv: 'skådespelare ingår inte',
      project: { videos: '1–4 redigerade filmer med en total längd upp till 5 minuter, ingen inspelning', actors: 'ingår inte', gfx: 'Bas', delivery: 'enligt gemensam projektplan' } },
    { key: 'Jones', en: 'Animation', sv: 'Animering', monthly: 45000,
      project: { videos: '2D-animering, upp till 1 min inkl. voice-over – ingen inspelning', actors: 'ingår inte (voice-over ingår)', gfx: '2D-animering', delivery: 'enligt gemensam projektplan' } },
    { key: 'Akerman', en: 'Custom', sv: 'Skräddarsytt', monthly: null, custom: true, project: null }
  ],
  tiers: [
    { min: 1, pct: 0, prices: { Bergman: 40000, Spielberg: 56000, Hitchcock: 75000, Kubrick: 150000, Scott: 40000, Lucas: 25000, Jones: 56000 } },
    { min: 3, pct: 10, prices: { Bergman: 36000, Spielberg: 50000, Hitchcock: 67500, Kubrick: 135000, Scott: 36000, Lucas: 22500, Jones: 50000 } },
    { min: 6, pct: 20, prices: { Bergman: 32000, Spielberg: 45000, Hitchcock: 60000, Kubrick: 120000, Scott: 32000, Lucas: 20000, Jones: 45000 } },
    { min: 12, pct: 27, prices: { Bergman: 29000, Spielberg: 40000, Hitchcock: 55000, Kubrick: 110000, Scott: 29000, Lucas: 18000, Jones: 40000 } }
  ],
  addOns: [
    { key: 'hour', en: 'Extra shoot, editing or admin time', sv: 'Extra inspelnings-, redigerings- eller administrationstid', price: 1500, unit: 'h', from: false, defaultOn: true },
    { key: 'talent', en: 'Actor or voice-over', sv: 'Skådespelare eller speaker', price: 5000, unit: null, from: true, defaultOn: true },
    { key: 'express', en: 'Express delivery', sv: 'Expressleverans', price: 7500, unit: null, from: false, defaultOn: true },
    { key: 'dunder', en: 'Dunderleverans (fastest delivery)', sv: 'Dunderleverans', price: 12500, unit: null, from: false, defaultOn: true },
    { key: 'reschedule', en: 'Rescheduling less than 7 days before the shoot', sv: 'Ombokning mindre än 7 dagar före inspelning', price: 5000, unit: null, from: false, defaultOn: true }
  ],
  startup: { subscription: 15000, project: 9500 },
  ads: { fixed: 20000, pct: 15, cap: 150000 },
  included: { en: '2 drafts, music, subtitles and up to 3 formats', sv: '2 drafts, musik, undertexter och upp till 3 format' }
};

export const PRICE_LIST_SEED_NOTE =
  "KFF's list from the quote templates and the price model (2026-10-02) · Kubrick monthly confirmed 2026-10-03 · Scott = Bergman ladder, Lucas confirmed 2026-10-05 · Jones priced as Spielberg 2026-10-06";

const int = (n: unknown) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 10_000_000;

/** Problems with a price list before it is saved as a new version (empty = fine). */
export function validatePriceList(d: PriceListData): string[] {
  const out: string[] = [];
  const keys = new Set<string>();
  for (const p of d.packages ?? []) {
    if (!p.key || keys.has(p.key)) out.push(`Package ${p.key || '(no name)'} is listed twice or has no name`);
    keys.add(p.key);
    if (p.custom ? p.monthly !== null : p.monthly !== null && !int(p.monthly)) out.push(`${p.key}: the monthly price must be a whole number of kronor`);
  }
  if (!keys.size) out.push('The list has no packages');
  const tiers = d.tiers ?? [];
  if (!tiers.length || tiers[0]!.min !== 1) out.push('The first volume tier must start at 1 production');
  tiers.forEach((t, i) => {
    if (i > 0 && t.min <= tiers[i - 1]!.min) out.push('Volume tiers must rise');
    if (!int(t.pct) || t.pct > 100) out.push(`Tier ${t.min}: the discount label must be 0–100 %`);
    for (const [k, v] of Object.entries(t.prices ?? {})) {
      if (!keys.has(k)) out.push(`Tier ${t.min}: unknown package ${k}`);
      else if (!int(v) || v <= 0) out.push(`Tier ${t.min}: ${k} needs a price above 0`);
    }
  });
  for (const a of d.addOns ?? []) if (!a.key || !int(a.price)) out.push(`Add-on ${a.en || a.key}: the price must be a whole number of kronor`);
  if (!int(d.startup?.subscription) || !int(d.startup?.project)) out.push('Start-up fees must be whole numbers of kronor');
  if (!int(d.ads?.fixed) || !int(d.ads?.cap) || !(typeof d.ads?.pct === 'number' && d.ads.pct >= 0 && d.ads.pct <= 100)) out.push('Ads management: fixed fee, percent and cap are needed');
  return out;
}

export const findPackage = (pl: PriceListData, key: string | null | undefined): PackageDef | null => pl.packages.find((p) => p.key === key) ?? null;

/** The tier that applies to a minimum number of productions (the highest tier whose minimum is reached). */
export function tierFor(pl: PriceListData, minProductions: number | null | undefined): TierDef | null {
  const n = Math.max(1, Math.floor(minProductions ?? 1));
  const ts = pl.tiers.slice().sort((a, b) => a.min - b.min);
  return ts.filter((t) => t.min <= n).pop() ?? ts[0] ?? null;
}

/** "1–2", "3–5", "6–11", "12+" */
export function tierRange(pl: PriceListData, t: TierDef): string {
  const ts = pl.tiers.slice().sort((a, b) => a.min - b.min);
  const nx = ts[ts.indexOf(t) + 1];
  return nx ? (nx.min - 1 > t.min ? `${t.min}–${nx.min - 1}` : String(t.min)) : `${t.min}+`;
}

/** Packages offered for a kind: everything with a price for it, plus the custom ones. */
export function packagesFor(pl: PriceListData, kind: AgreementKind): PackageDef[] {
  const t1 = tierFor(pl, 1);
  return pl.packages.filter((p) => p.custom || (kind === 'subscription' ? (p.monthly ?? 0) > 0 : (t1?.prices[p.key] ?? 0) > 0));
}

export interface ListPrice {
  /** List price per month (subscription) or per production at the tier (project); null = custom or not on the list */
  price: number | null;
  custom: boolean;
  tier: TierDef | null;
}

export function listPrice(pl: PriceListData, kind: AgreementKind, packageKey: string, minProductions?: number | null): ListPrice {
  const p = findPackage(pl, packageKey);
  if (!p || p.custom) return { price: null, custom: Boolean(p?.custom), tier: null };
  if (kind === 'subscription') return { price: p.monthly, custom: false, tier: null };
  const tier = tierFor(pl, minProductions);
  return { price: tier?.prices[p.key] ?? null, custom: false, tier };
}

/** Ads management per month: fixed + pct % of the media budget, the variable part counted on at most the cap. */
export const adsFee = (pl: PriceListData, mediaBudget: number): number => pl.ads.fixed + Math.round((Math.min(Math.max(0, mediaBudget), pl.ads.cap) * pl.ads.pct) / 100);

export interface QuoteTerms {
  kind: AgreementKind;
  packageKey: string;
  /** Project: the minimum number of productions (sets the tier) */
  minProductions: number | null;
  /** Subscription: binding period (null = ongoing); project: the period the minimum applies to (default 2 months) */
  periodMonths: number | null;
  /**
   * Before 2026-10-30, the packages a project agreement let the client call off. Every agreement now lists all packages
   * (owner 2026-10-30: "alla avtal behöver innehålla samtliga paket och priser"), so this is kept but no longer limits.
   */
  callOffKeys?: string[] | null;
  /** The agreed price per package where it is not the list price (per month or per production); the chosen package's is `price` */
  packagePrices?: Record<string, number> | null;
  /** Price per month (subscription) or per production (project) */
  price: number | null;
  priceOverrideReason: string | null;
  startupFee: number | null;
  /** Akerman: what the custom package contains */
  customContents: string | null;
  /**
   * Options in the agreement (owner 2026-10-16): extras the client can call off when needed during the agreement, at the
   * agreed price. Not part of the committed value. `qty` is from quotes made before the change and is ignored.
   */
  addOns: { key: string; price: number; qty?: number }[];
  /** Ads management with this monthly media budget (null = none) */
  adsMediaBudget: number | null;
  startDate: string | null;
  /** The client as party to the agreement (org.nr, address, who signs); filled in before the agreement is signed */
  party?: ContractParty | null;
  /** Special terms for this agreement only; they go into the agreement and win over its template and the general terms */
  specialTerms?: string | null;
  /**
   * Travel (owner 2026-10-29): null = the standard (inside Stockholm included; outside the cost plus the travel time at half
   * the hourly price, Allmänna villkor 6.1). A deviation is written into the agreement as a special term.
   */
  travel?: TravelRule | null;
  /** Sales board (owner 2026-10-27): a rough value for a deal added before the quote is priced; shown until it is sent */
  estimate?: number | null;
  /** Won deal handed over to the producer (owner 2026-10-27): who, when, and the client note with the summary */
  handover?: { at: string; producerId: string; noteId: string | null; by: string | null } | null;
}

/** The client as party to the agreement, kept in the quote's terms. */
export interface ContractParty {
  orgNo: string | null;
  address: string | null;
  signerName: string | null;
  signerTitle: string | null;
}

export interface QuoteLine {
  key: string;
  en: string;
  sv: string;
  qty: number;
  unit: 'month' | 'production' | 'once' | 'h';
  unitPrice: number;
  amount: number;
}

/** A package the client can call off on a project-based agreement, at its price per production. */
export interface CallOffPrice {
  key: string;
  en: string;
  sv: string;
  price: number;
  /** The minimum package (its price can be set by hand on the quote) */
  minimum: boolean;
}

/** Project defaults (owner 2026-10-15): at least 1 production within 2 months. */
export const PROJECT_DEFAULTS = { minProductions: 1, periodMonths: 2 } as const;

/**
 * A project agreement with no minimum (owner 2026-10-29: "ramavtal med 0 minimum"): the client calls off at the agreed
 * prices when it needs to, with nothing committed and no shortfall. Minimum 0 on the quote; prices at the first tier.
 */
export const isFrameOnly = (t: Pick<QuoteTerms, 'kind' | 'minProductions'>) => t.kind === 'project' && t.minProductions === 0;

/** The minimum count on a project agreement: 0 for a frame agreement, else at least 1 (old quotes without one: 1). */
export const minProductionsOf = (t: Pick<QuoteTerms, 'minProductions'>) => (t.minProductions === 0 ? 0 : Math.max(1, t.minProductions ?? 1));

/** "Minst 6 produktioner inom 12 månader" / "Ramavtal utan minimiåtagande" */
export function minimumSv(t: Pick<QuoteTerms, 'kind' | 'minProductions' | 'periodMonths'>): string {
  if (isFrameOnly(t)) return 'Ramavtal utan minimiåtagande: avrop till avtalade priser vid behov';
  const n = minProductionsOf(t);
  const m = t.periodMonths ?? PROJECT_DEFAULTS.periodMonths;
  return `Minst ${n} ${n === 1 ? 'produktion' : 'produktioner'} inom ${m} ${m === 1 ? 'månad' : 'månader'}`;
}

/**
 * The key terms a quote summarizes, from the client agreement templates (docs/avtalsmallar-2026-10-02.md) and the owner's
 * decisions. The full text comes with the agreement and KFF's general terms.
 */
export const QUOTE_TERMS = {
  paymentDays: 30,
  noticeMonths: 3,
  cancelDays: 7,
  cancelEarlyPct: 20,
  cancelLatePct: 50,
  generalTermsVersion: '2026:2',
} as const;

/**
 * The packages on a project-based quote, in price list order: the minimum package plus the ones picked for call-off, each at
 * the tier price for the agreed minimum. The minimum package uses the quote's own price when one is set.
 */
/**
 * Every package in an agreement with its agreed price (owner 2026-10-30): per month for a subscription, per production at
 * the agreement's tier for a project agreement. The chosen package is priced by `price`, any other by `packagePrices`,
 * else the list. The client can switch package any month (subscription) or call off any of them (project).
 */
export interface AgreementPackage extends CallOffPrice {
  /** the list price at this kind and tier, to show what was agreed against it */
  list: number | null;
}
export function agreementPackages(pl: PriceListData, t: Pick<QuoteTerms, 'kind' | 'packageKey' | 'minProductions' | 'price' | 'packagePrices'>): AgreementPackage[] {
  const tier = t.kind === 'project' ? tierFor(pl, t.minProductions) : null;
  const out: AgreementPackage[] = [];
  for (const p of pl.packages) {
    const minimum = p.key === t.packageKey;
    if (p.custom && !minimum) continue;
    const list = t.kind === 'subscription' ? p.monthly : (tier?.prices[p.key] ?? null);
    const price = (minimum && t.price !== null ? t.price : null) ?? t.packagePrices?.[p.key] ?? list;
    if (price) out.push({ key: p.key, en: p.en, sv: p.sv, price, minimum, list });
  }
  return out;
}

/** A project agreement's packages at their price per production (empty for a subscription). */
export function callOffPrices(pl: PriceListData, t: Pick<QuoteTerms, 'kind' | 'packageKey' | 'minProductions' | 'price' | 'packagePrices'>): CallOffPrice[] {
  return t.kind === 'project' ? agreementPackages(pl, t).map(({ list: _list, ...c }) => c) : [];
}

/** The next tier up from the agreed minimum, to show what committing to more would give (null = already on the top tier). */
export function nextTier(pl: PriceListData, minProductions: number | null | undefined): TierDef | null {
  const cur = tierFor(pl, minProductions);
  return pl.tiers.slice().sort((a, b) => a.min - b.min).find((x) => cur && x.min > cur.min) ?? null;
}

/** An option in the agreement with its agreed price. */
export interface QuoteOption {
  key: string;
  en: string;
  sv: string;
  price: number;
  unit: 'h' | null;
  from: boolean;
}

/** The options a new quote starts with: the price list's default ones at list price. */
export const defaultAddOns = (pl: PriceListData): { key: string; price: number }[] => pl.addOns.filter((a) => isDefaultOn(a)).map((a) => ({ key: a.key, price: a.price }));

/** Lists saved before 2026-10-16 have no default flag: those add-ons follow the seed. */
export const isDefaultOn = (a: AddOnDef): boolean => a.defaultOn ?? PRICE_LIST_SEED.addOns.find((x) => x.key === a.key)?.defaultOn ?? false;

export interface QuoteSummary {
  pkg: PackageDef | null;
  /** Options the client can call off when needed, in price list order (not in the totals) */
  options: QuoteOption[];
  /** Project: every package the client can call off with its price per production (empty for a subscription) */
  callOff: CallOffPrice[];
  list: ListPrice;
  /** Start-up fee on the list: only for a new client */
  listStartup: number;
  priceDiffers: boolean;
  startupDiffers: boolean;
  lines: QuoteLine[];
  /** Recurring per month (subscription fee + ads management) */
  monthly: number;
  /** One-off amounts (start-up fee and add-ons) */
  oneOff: number;
  /** The committed value: subscription = monthly × binding period, project = productions × price (+ one-offs) */
  committed: number | null;
  /** What still has to be filled in before the quote can be sent */
  missing: string[];
}

const svNum = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
/** "30 000 kr" */
export const kr = (n: number) => `${svNum(n)} kr`;

export function summarizeQuote(pl: PriceListData, t: QuoteTerms, opts: { newClient: boolean }): QuoteSummary {
  const pkg = findPackage(pl, t.packageKey);
  const list = listPrice(pl, t.kind, t.packageKey, t.minProductions);
  const listStartup = opts.newClient ? pl.startup[t.kind] : 0;
  const price = t.price ?? list.price;
  const startup = t.startupFee ?? listStartup;
  const priceDiffers = !list.custom && list.price !== null && price !== null && price !== list.price;
  const startupDiffers = startup !== listStartup;
  const lines: QuoteLine[] = [];
  const missing: string[] = [];
  if (!pkg) missing.push('Choose a package');
  if (pkg?.custom && !t.customContents?.trim()) missing.push('Describe what the custom package contains');
  if (price === null || price <= 0) missing.push(t.kind === 'subscription' ? 'Set the monthly price' : 'Set the price per production');
  if (t.kind === 'project' && !(t.minProductions !== null && t.minProductions >= 0)) missing.push('Set the minimum number of productions');
  if ((priceDiffers || startupDiffers) && !t.priceOverrideReason?.trim()) missing.push('Give a reason for differing from the price list');
  const name = pkg ? `${pkg.key} (${pkg.sv})` : t.packageKey;
  let monthly = 0;
  let committed: number | null = null;
  if (price !== null && price > 0) {
    if (t.kind === 'subscription') {
      lines.push({ key: 'fee', en: `${pkg?.key ?? t.packageKey} – monthly fee`, sv: `${name} – månadsavgift`, qty: 1, unit: 'month', unitPrice: price, amount: price });
      monthly += price;
    } else {
      // a frame agreement commits to nothing: the line shows the price of one production
      const n = minProductionsOf(t);
      lines.push({ key: 'fee', en: `${pkg?.key ?? t.packageKey} – per production`, sv: `${name} – per produktion`, qty: Math.max(1, n), unit: 'production', unitPrice: price, amount: price * Math.max(1, n) });
      committed = price * n;
    }
  }
  if (t.adsMediaBudget !== null && t.adsMediaBudget >= 0) {
    const fee = adsFee(pl, t.adsMediaBudget);
    lines.push({ key: 'ads', en: 'Ads management', sv: 'Annonshantering', qty: 1, unit: 'month', unitPrice: fee, amount: fee });
    monthly += fee;
  }
  let oneOff = 0;
  if (startup > 0) {
    lines.push({ key: 'startup', en: 'Start-up fee', sv: 'Startavgift', qty: 1, unit: 'once', unitPrice: startup, amount: startup });
    oneOff += startup;
  }
  const options: QuoteOption[] = [];
  for (const def of pl.addOns) {
    const a = t.addOns.find((x) => x.key === def.key);
    if (a) options.push({ key: def.key, en: def.en, sv: def.sv, price: a.price, unit: def.unit, from: def.from });
  }
  if (t.kind === 'subscription' && t.periodMonths && monthly > 0) committed = monthly * t.periodMonths;
  if (committed !== null) committed += oneOff;
  return { pkg, options, callOff: callOffPrices(pl, { ...t, price }), list, listStartup, priceDiffers, startupDiffers, lines, monthly, oneOff, committed, missing };
}

/** The package contents in Swedish for the quote document. */
export function contentsSv(pl: PriceListData, pkg: PackageDef, kind: AgreementKind, customContents?: string | null): string[] {
  if (pkg.custom) return [customContents?.trim() || 'Innehållet beskrivs i offerten.'];
  const out: string[] = [];
  if (kind === 'subscription' && pkg.filmsSv) {
    out.push(pkg.shootH ? `Inspelning upp till ${pkg.shootH} timmar per månad` : 'Ingen inspelning');
    out.push(`${pkg.filmsSv} per månad, med en total längd upp till ${pkg.lengthMin} minuter`);
    if (pkg.gfxSv) out.push(`Grafik: ${pkg.gfxSv}`);
    if (pkg.actorsSv) out.push(pkg.actorsSv[0]!.toUpperCase() + pkg.actorsSv.slice(1));
  } else if (pkg.project) {
    const per = kind === 'subscription' ? 'per månad' : 'per produktion';
    out.push(`Video ${per}: ${pkg.project.videos}`);
    out.push(`Skådespelare: ${pkg.project.actors}`);
    out.push(`Grafik: ${pkg.project.gfx}`);
    out.push(`Leverans: ${pkg.project.delivery}`);
  }
  out.push(`Ingår alltid: ${pl.included.sv}`);
  return out;
}
